import type { Blog } from '@/payload-types'
import { BlogCard } from '@/components/blog'
import { PenLine, ArrowRight } from 'lucide-react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'

type BlogGridBlockProps = {
  blockType: 'blogGrid'
  id?: string | null
  label?: string | null
  heading?: string | null
  description?: string | null
  limit?: number | null
  showAllButton?: boolean | null
  showBottomCTA?: boolean | null
  blogs?: (Blog | number)[] | null
}

export const BlogGridBlockComponent: React.FC<BlogGridBlockProps> = ({
  label,
  heading,
  description,
  limit,
  showAllButton,
  showBottomCTA,
  blogs,
}) => {
  // Filter to only populated blogs (not just IDs) and apply limit
  const populatedBlogs = (blogs || []).filter(
    (b): b is Blog => typeof b === 'object' && b !== null,
  )
  const blogDocs = limit ? populatedBlogs.slice(0, limit) : populatedBlogs

  // Don't render the section if there are no blogs
  if (blogDocs.length === 0) return null

  return (
    <section className="pt-8 pb-8 md:pt-12 md:pb-12" aria-label="Blog" id="blog-section">
      <div className="container">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div className="space-y-3">
            {label && (
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">
                {label}
              </p>
            )}
            {heading && (
              <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                {heading}
              </h2>
            )}
            {description && <p className="max-w-lg text-muted-foreground">{description}</p>}
          </div>
          {showAllButton && (
            <Button asChild variant="outline" className="rounded-full">
              <Link href="/blog" className="inline-flex items-center gap-2">
                <PenLine className="h-4 w-4" />
                Bekijk alle artikelen
              </Link>
            </Button>
          )}
        </div>

        <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
          {blogDocs.map((blog) => (
            <BlogCard blog={blog} key={blog.id} />
          ))}
        </div>

        {showBottomCTA && (
          <div className="mt-12 text-center">
            <Button asChild size="lg" className="rounded-full px-8">
              <Link href="/blog" className="inline-flex items-center gap-2">
                Bekijk alle artikelen
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}
